import React from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../hooks/useContent';

const F = "'Inter', system-ui, sans-serif";

export default function Leistungen() {
  const { content, loading, error } = useContent();

  if (loading) return <div style={styles.loading}>Laden…</div>;
  if (error) return <div style={styles.loading}>Inhalte konnten nicht geladen werden.</div>;

  const leistungen = content.leistungen || [];

  return (
    <main style={styles.page}>
      <div style={styles.pageHeader}>
        <div style={styles.pageHeaderInner}>
          <span style={styles.eyebrow}>Unterkünfte & Stellplätze</span>
          <h1 style={styles.heading}>Unsere Leistungen</h1>
          <p style={styles.sub}>Für jeden Geschmack das passende Angebot – von rustikal bis komfortabel.</p>
        </div>
      </div>

      <div style={styles.inner}>
        {leistungen.map((item, index) => (
          <ServiceCard key={item.id} item={item} reverse={index % 2 === 1} />
        ))}

        <div style={styles.cta}>
          <h2 style={styles.ctaHeading}>Bereit für Ihren Aufenthalt?</h2>
          <p style={styles.ctaText}>Sichern Sie sich jetzt Ihren Wunschplatz – wir freuen uns auf Ihren Besuch!</p>
          <Link to="/buchung" style={styles.ctaBtn}>Jetzt anfragen</Link>
        </div>
      </div>
    </main>
  );
}

function ServiceCard({ item, reverse }) {
  const hasPhoto = !!item.photo;

  return (
    <div style={{ ...styles.card, flexDirection: reverse ? 'row-reverse' : 'row' }}>
      {hasPhoto ? (
        <div style={styles.photoWrapper}>
          <img src={item.photo} alt={item.title} style={styles.photo} />
        </div>
      ) : (
        <div style={styles.iconBox}>
          <span style={styles.iconEmoji}>{item.icon}</span>
        </div>
      )}

      <div style={styles.cardBody}>
        <p style={styles.cardSubtitle}>{item.subtitle}</p>
        <h2 style={styles.cardTitle}>{item.title}</h2>
        <p style={styles.cardDesc}>{item.description}</p>

        {item.features && item.features.length > 0 && (
          <ul style={styles.featureList}>
            {item.features.map((f, i) => (
              <li key={i} style={styles.featureItem}>
                <span style={styles.checkmark}>✓</span>
                {f}
              </li>
            ))}
          </ul>
        )}

        {item.capacity && (
          <div style={styles.capacityBadge}>
            <span style={styles.capacityLabel}>Kapazität:</span>
            <span style={styles.capacityValue}>{item.capacity}</span>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { backgroundColor: 'white', minHeight: '70vh' },

  pageHeader: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e5e7eb',
    padding: '4rem 1.5rem 3.5rem',
    textAlign: 'center',
  },
  pageHeaderInner: { maxWidth: '640px', margin: '0 auto' },
  eyebrow: { fontSize: '0.75rem', fontFamily: F, fontWeight: '600', letterSpacing: '1.8px', textTransform: 'uppercase', color: '#1e8449' },
  heading: { color: '#111827', fontFamily: F, fontSize: 'clamp(1.9rem,5vw,2.8rem)', fontWeight: '800', marginTop: '0.4rem', marginBottom: '0.7rem', letterSpacing: '-0.5px' },
  sub: { color: '#6b7280', fontFamily: F, fontSize: '1rem' },

  inner: { maxWidth: '900px', margin: '0 auto', padding: '3.5rem 1.5rem 5rem' },

  card: {
    display: 'flex',
    gap: '3rem',
    marginBottom: '4rem',
    paddingBottom: '4rem',
    borderBottom: '1px solid #f3f4f6',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },

  /* Photo variant */
  photoWrapper: {
    flexShrink: 0,
    width: '300px',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 6px 24px rgba(0,0,0,0.10)',
  },
  photo: { width: '100%', height: '220px', objectFit: 'cover', display: 'block' },

  /* Icon fallback */
  iconBox: {
    flexShrink: 0,
    width: '100px',
    height: '100px',
    borderRadius: '20px',
    backgroundColor: '#f0fdf4',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconEmoji: { fontSize: '3rem', lineHeight: 1 },

  cardBody: { flex: 1, minWidth: '260px' },
  cardSubtitle: { color: '#1e8449', fontFamily: F, fontSize: '0.8rem', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '0.3rem' },
  cardTitle: { color: '#111827', fontFamily: F, fontSize: '1.6rem', fontWeight: '800', marginBottom: '0.75rem', letterSpacing: '-0.3px' },
  cardDesc: { color: '#4b5563', fontFamily: F, fontSize: '0.97rem', lineHeight: '1.75', marginBottom: '1.25rem' },

  featureList: { listStyle: 'none', padding: 0, margin: '0 0 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  featureItem: { display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151', fontFamily: F, fontSize: '0.9rem' },
  checkmark: { color: '#1e8449', fontWeight: '700', flexShrink: 0 },

  capacityBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    backgroundColor: '#f0fdf4',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    border: '1px solid #bbf7d0',
  },
  capacityLabel: { color: '#6b7280', fontFamily: F, fontSize: '0.83rem' },
  capacityValue: { color: '#15803d', fontFamily: F, fontSize: '0.9rem', fontWeight: '600' },

  cta: {
    backgroundColor: '#f0fdf4',
    borderRadius: '20px',
    padding: '3.5rem 2rem',
    textAlign: 'center',
    marginTop: '1rem',
  },
  ctaHeading: { color: '#111827', fontFamily: F, fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.6rem', letterSpacing: '-0.2px' },
  ctaText: { color: '#4b5563', fontFamily: F, fontSize: '0.97rem', marginBottom: '1.75rem' },
  ctaBtn: {
    display: 'inline-block',
    backgroundColor: '#1e8449',
    color: 'white',
    padding: '0.85rem 2.2rem',
    borderRadius: '10px',
    textDecoration: 'none',
    fontFamily: F,
    fontWeight: '600',
    fontSize: '0.95rem',
    boxShadow: '0 2px 8px rgba(30,132,73,0.25)',
  },

  loading: { padding: '4rem', textAlign: 'center', color: '#6b7280', fontFamily: F },
};
